import classNames from 'classnames';
import * as styles from './Reader.module.scss';
import { useEffect, useRef, useState } from 'react';

export const BarcodeDetector = ({ onScan, deviceId }) => {
    const isMountedRef = useRef(true);
    const videoRef = useRef(null);
    const animationFrameRef = useRef(null);
    const processingRef = useRef(false);
    const restartTimeoutRef = useRef(null);
    const streamRef = useRef(null);
    const rectRef = useRef(null);
    const containerRef = useRef(null);
    const barcodeDetectorRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [supported, setSupported] = useState(true);

    const checkSupport = () => {
        if (!('BarcodeDetector' in window)) {
            setSupported(false);
            return false;
        }

        try {
            barcodeDetectorRef.current = new window.BarcodeDetector({
                formats: ['qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'codabar', 'data_matrix', 'pdf417', 'aztec']
            });
            return true;
        } catch (error) {
            alert(error?.message)
            setSupported(false);
            return false;
        }
    };

    const clearScanner = () => {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;

        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current?.srcObject) {
            videoRef.current.srcObject = null;
        }
    };

    const startScanner = async (selectedDeviceId, retry) => {
        if (!isMountedRef.current || !supported) return;

        setLoading(true);
        clearScanner();

        await new Promise(res => setTimeout(res, 100));

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: selectedDeviceId ? {
                    deviceId: { exact: selectedDeviceId },
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                } : {
                    facingMode: 'environment',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                },
                audio: false
            });

            if (!isMountedRef.current) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }

            clearTimeout(restartTimeoutRef.current);

            let flag = false;
            stream.getTracks().forEach(track => {
                if (track.readyState === 'ended') flag = true;
            });
            if (flag) {
                return startScanner(deviceId, retry);
            }

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
                startProcessing();
            }
        } catch (error) {
            if (retry > 6) {
                return;
            }
            if (!isMountedRef.current) return;

            clearTimeout(restartTimeoutRef.current);
            restartTimeoutRef.current = setTimeout(() => startScanner(selectedDeviceId, retry + 1), 400);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    const restartScanner = async (selectedDeviceId) => {
        if (loading && !selectedDeviceId) return;
        await startScanner(selectedDeviceId, 0);
    };

    const processFrame = async () => {
        if (!isMountedRef.current || !videoRef.current || processingRef.current || !barcodeDetectorRef.current) {
            return;
        }

        processingRef.current = true;

        try {
            const video = videoRef.current;

            if (video.readyState < 2) {
                return;
            }

            const barcodes = await barcodeDetectorRef.current.detect(video);

            if (barcodes && barcodes.length > 0) {
                const barcode = barcodes[0];

                if (barcode.boundingBox) {
                    displayRect(barcode.boundingBox);
                }

                onScan({
                    result: {
                        text: barcode.rawValue,
                        format: barcode.format,
                        position: barcode.boundingBox
                    }
                });
            }
        } finally {
            processingRef.current = false;
            if (isMountedRef.current) {
                animationFrameRef.current = requestAnimationFrame(processFrame);
            }
        }
    };

    const startProcessing = () => {
        if (!isMountedRef.current || !supported) return;
        animationFrameRef.current = requestAnimationFrame(processFrame);
    };

    const displayRect = (boundingBox) => {
        if (!containerRef.current) return;
        if (!boundingBox) return;

        const video = videoRef.current;
        const container = containerRef.current;
        const videoRect = container.getBoundingClientRect();
        const scaleX = videoRect.width / video.videoWidth;
        const scaleY = videoRect.height / video.videoHeight;

        const style = {
            left: boundingBox.x * scaleX + 'px',
            top: boundingBox.y * scaleY + 'px',
            width: boundingBox.width * scaleX + 'px',
            height: boundingBox.height * scaleY + 'px',
        };

        clearRect();
        setTimeout(clearRect, 200);

        rectRef.current = document.createElement('div');
        rectRef.current.className = styles.codeRect;
        container.appendChild(rectRef.current);

        Object.assign(rectRef.current.style, style);

        rectRef.current.dataset.rect = JSON.stringify({
            x: boundingBox.x,
            y: boundingBox.y,
            width: boundingBox.width,
            height: boundingBox.height
        });
    };

    const clearRect = () => {
        if (rectRef.current) {
            rectRef.current.remove();
            rectRef.current = null;
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (rectRef.current) {
                try {
                    const rect = JSON.parse(rectRef.current.dataset.rect);
                    displayRect(rect);
                } catch (e) {

                }
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            isMountedRef.current = false;
            clearScanner();
            clearRect();
        };
    }, []);

    useEffect(() => {
        const isApiSupported = checkSupport();
        if (!isApiSupported) {
            setLoading(false);
            return;
        }

        if (deviceId) {
            clearTimeout(restartTimeoutRef.current);
            restartScanner(deviceId);
        }
    }, [deviceId]);

    if (!supported) {
        return (
            <div className={classNames(styles.reader, 'uikit_reader')} ref={containerRef}>
                <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#666',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px'
                }}>
                    Barcode Detection API не поддерживается в вашем браузере.
                </div>
            </div>
        );
    }

    return (
        <div className={classNames(styles.reader, 'uikit_reader')} ref={containerRef}>
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: 'auto' }}
            />
            {loading && (
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    padding: '10px 20px',
                    borderRadius: '4px'
                }}>
                    Загрузка...
                </div>
            )}
        </div>
    );
};